import { motion } from 'framer-motion'
import type { PlayingCard as PlayingCardType } from '@/types'
import { cn } from '@/lib/utils'

interface PlayingCardProps {
  card: PlayingCardType
  animate?: boolean
  delay?: number
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const sizeClasses = {
  sm: 'w-12 h-[72px] text-base',
  md: 'w-16 h-24 text-xl',
  lg: 'w-20 h-[120px] text-2xl',
}

export function PlayingCard({
  card,
  animate = true,
  delay = 0,
  size = 'md',
  className,
}: PlayingCardProps) {
  const cardContent = (
    <div
      className={cn(
        'bg-white rounded-lg flex flex-col items-center justify-center font-bold shadow-lg border border-gray-200 relative overflow-hidden',
        sizeClasses[size],
        card.color === 'red' ? 'text-red-600' : 'text-gray-900',
        className
      )}
    >
      {/* Top-left corner */}
      <div className="absolute top-1 left-1 text-xs leading-none">
        <div>{card.value}</div>
        <div>{card.suit}</div>
      </div>

      {/* Center */}
      <span className="text-2xl">{card.suit}</span>

      {/* Bottom-right corner (rotated) */}
      <div className="absolute bottom-1 right-1 text-xs leading-none rotate-180">
        <div>{card.value}</div>
        <div>{card.suit}</div>
      </div>
    </div>
  )

  if (!animate) {
    return cardContent
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 100, rotateY: -90 }}
      animate={{ opacity: 1, x: 0, rotateY: 0 }}
      transition={{
        duration: 0.4,
        delay,
        type: 'spring',
        stiffness: 200,
        damping: 20,
      }}
    >
      {cardContent}
    </motion.div>
  )
}

// Card back (for hidden cards)
export function CardBack({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg'; className?: string }) {
  return (
    <div
      className={cn(
        'rounded-lg shadow-lg overflow-hidden',
        sizeClasses[size],
        className
      )}
    >
      <img
        src="/img/card-back.png"
        alt="Card back"
        className="w-full h-full object-cover"
      />
    </div>
  )
}
